import { useColabNovidades } from "@/hooks/useColabNovidades";
import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, CalendarDays, FolderKanban, Menu, Inbox, CalendarClock, Users, Settings as SettingsIcon, LogOut, Calculator, ShieldCheck, UsersRound, MessageSquare, Building2, Gauge } from "lucide-react";
import { useMensagens } from "@/context/MensagensContext";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import GlobalSearch from "@/components/GlobalSearch";

// Os 4 itens fixos da barra inferior (mobile) — os mais acessados no dia a dia
// (Lote 21, corrigindo o Lote 20: Calendário volta pra cá, e Audiências+Prazos viram
// uma aba só, "Agenda", pra abrir espaço pra "Publicações" sem perder nenhuma das duas).
const TABS = [
  { to: "/dashboard", label: "Painel", icon: LayoutDashboard, testid: "tab-dashboard" },
  { to: "/agenda", label: "Agenda", icon: CalendarClock, testid: "tab-agenda" },
  { to: "/calendario", label: "Calendário", icon: CalendarDays, testid: "tab-calendario" },
  { to: "/publicacoes", label: "Publicações", icon: Inbox, testid: "tab-publicacoes" },
];

const MORE_LINKS = [
  { to: "/revisao", label: "Para validar", icon: Inbox },
  { to: "/calculadora-prazos", label: "Calculadora de prazos", icon: Calculator },
  { to: "/processos", label: "Processos", icon: FolderKanban },
  { to: "/clientes", label: "Clientes", icon: Users },
  { to: "/compartilhamentos", label: "Compartilhamentos", icon: UsersRound },
  { to: "/mensagens", label: "Chat", icon: MessageSquare },
  { to: "/config", label: "Configurações", icon: SettingsIcon },
];

// Igual ao Layout.jsx (desktop): só entra pra quem tem is_owner=true. Ficava de fora
// no mobile por engano — o MORE_LINKS é uma lista à parte do menu do desktop, então um
// item novo (ou owner-only, como este) precisa ser adicionado nos dois lugares.
const escritorioLink = { to: "/escritorio", label: "Escritório", icon: Building2 };
const controladoriaLink = { to: "/controladoria", label: "Controladoria", icon: Gauge };      // Lote 164
const adminLink = { to: "/admin", label: "Administração", icon: ShieldCheck };

function TabButton({ to, label, icon: Icon, testid }) {
  return (
    <NavLink
      to={to}
      data-testid={testid}
      className={({ isActive }) =>
        `flex flex-col items-center justify-center gap-1 flex-1 h-full text-[11px] font-semibold rounded-lg mx-1 my-1.5 transition-colors ${
          isActive ? "text-gold-400 bg-gold-500/15" : "text-stone-500"
        }`
      }
    >
      <Icon className="w-5 h-5" strokeWidth={1.75} />
      {label}
    </NavLink>
  );
}

export default function MobileTabBar() {
  const [moreOpen, setMoreOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { unread, convites } = useMensagens();
  const novidadesColab = useColabNovidades();
  const extrasEsc = [...((user?.escritorio || user?.is_owner) ? [escritorioLink] : []), ...(user?.escritorio?.controladoria ? [controladoriaLink] : [])];
  const base = extrasEsc.length ? [...MORE_LINKS.slice(0, -1), ...extrasEsc, MORE_LINKS[MORE_LINKS.length - 1]] : MORE_LINKS;
  const moreLinks = user?.is_owner ? [...base, adminLink] : base;

  return (
    <>
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-stone-900 border-t border-stone-800 flex items-stretch h-20 shadow-[0_-2px_10px_rgba(0,0,0,0.24)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="mobile-tab-bar"
      >
        {TABS.map((tab) => (
          <TabButton key={tab.to} {...tab} />
        ))}
        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          data-testid="tab-more"
          className="flex flex-col items-center justify-center gap-1 flex-1 h-full text-[11px] font-semibold text-stone-500"
        >
          <span className="relative">
            <Menu className="w-5 h-5" strokeWidth={1.75} />
            {novidadesColab > 0 && <span data-testid="tab-more-colab-dot" aria-label="Há novidades em Compartilhamentos" className="absolute -top-1 -left-2 h-2.5 w-2.5 rounded-full bg-[#c8502d]" />}
            {unread + convites > 0 && <span data-testid="tab-more-badge" className="absolute -top-1 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-gold-500 text-stone-950 text-[10px] font-bold inline-flex items-center justify-center">{unread + convites}</span>}
          </span>
          Mais
        </button>
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="md:hidden rounded-t-2xl">
          <SheetHeader>
            <SheetTitle className="text-left font-display">Mais</SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            <GlobalSearch variant="light" onNavigate={() => setMoreOpen(false)} />
          </div>
          <div className="grid grid-cols-2 gap-2 mt-4">
            {moreLinks.map(({ to, label, icon: Icon }) => (
              <button
                key={to}
                type="button"
                data-testid={`more-${to.slice(1)}`}
                onClick={() => { setMoreOpen(false); navigate(to); }}
                className="relative flex flex-col items-center justify-center gap-2 rounded-lg border border-stone-200 py-5 text-sm font-medium text-stone-700 active:bg-stone-100"
              >
                <Icon className="w-5 h-5 text-stone-500" strokeWidth={1.75} />
                {label}
                {to === "/compartilhamentos" && novidadesColab > 0 && (
                  <span data-testid="more-compartilhamentos-badge" className="absolute top-2 right-3 min-w-[18px] h-[18px] px-1 rounded-full bg-[#c8502d] text-white text-[11px] font-bold inline-flex items-center justify-center">{novidadesColab}</span>
                )}
                {to === "/mensagens" && unread + convites > 0 && (
                  <span className="absolute top-2 right-3 min-w-[18px] h-[18px] px-1 rounded-full bg-gold-500 text-stone-950 text-[11px] font-bold inline-flex items-center justify-center">{unread + convites}</span>
                )}
              </button>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-stone-200 flex items-center justify-between">
            <div className="min-w-0">
              <div className="text-sm font-medium text-stone-900 truncate">{user?.name}</div>
              <div className="text-xs text-stone-500 truncate">{user?.email}</div>
            </div>
            <button
              type="button"
              onClick={() => { setMoreOpen(false); logout(); }}
              data-testid="mobile-logout-button"
              className="flex items-center gap-1.5 text-sm font-medium text-rose-brand px-3 py-2"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.75} /> Sair
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
